import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Button, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Form,
  Select,
  Table,
  InputNumber,
  DatePicker,
  Input,
  Space,
  Empty,
  Descriptions,
  Divider,
  Button as AntButton,
  message,
  Upload,
  Popover,
  TableColumnsType,
  Row,
  Col,
} from "antd";
import { UploadOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { formatCurrency, indianCurrencyFormatter, indianCurrencyParser } from "../../utils/Utils";
import { PaymentMethod, PaymentType } from "./types";
import { mockPaymentMethodOptions } from "./mockData/paymentData";
import PaymentsApprovalDrawer from "./PaymentsApprovalDrawer";
import sequenceApis from "../../api/master/sequence";
import paymentApis from "../../api/WorkOrderManagement/PaymentsApis";

const { Option } = Select;
const { TextArea } = Input;

interface RetentionAllocation {
  id: string;
  workOrderNumber: string;
  billValue: number;
  paidTillDate: number;
  pendingApproval: number;
  balance: number;
  thisPaymentAmount: number;
}

const ReleaseRetentionForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const flowTypeFromUrl = searchParams.get("flowType");
  const {
    contractorId,
    contractorName,
    line,
    workOrderId,
    flowType: flowTypeFromState,
  } = location.state || {};
  const flowType = flowTypeFromUrl || flowTypeFromState || "Contractor";

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Allocations data
  const [allocations, setAllocations] = useState<RetentionAllocation[]>([]);

  // File upload
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Sequence and voucher
  const [sequenceAccess, setSequenceAccess] = useState<any>();
  const [paymentSequenceId, setPaymentSequenceId] = useState("");

  // Approval related state
  const [approvalRequired, setApprovalRequired] = useState<string>("false");
  const [approvals, setApprovals] = useState<{
    open: boolean;
    data: any[];
  }>({ open: false, data: [] });

  // Commercial calculations
  const [netPayable, setNetPayable] = useState(0);

  useEffect(() => {
    if (!contractorId) {
      message.error("Contractor information not found");
      navigate(-1);
      return;
    }
    initializeForm();
    initializeAllocations();
    if (line?.siteId) {
      fetchSequenceAccess(line.siteId);
    }
  }, [contractorId]);

  const initializeForm = () => {
    form.setFieldsValue({
      paymentDate: dayjs(),
      paymentType: PaymentType.RetentionRelease,
      contractorId: contractorId,
      contractorName: contractorName,
      approvalRequired: "false",
    });
  };

  const fetchSequenceAccess = async (siteId: number) => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Payments" &&
          new Date(item.endDate) > today &&
          item.status === "Active" &&
          item.siteId === siteId
      );
      if (_sequenceAccess.length > 0) {
        setSequenceAccess({
          module: "Payments",
          level: _sequenceAccess[0].level,
          siteIds: _sequenceAccess?.map((item: any) =>
            item?.siteId?.toString()
          ),
        });
      } else {
        setSequenceAccess({
          module: "",
          level: "",
          siteIds: [],
        });
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
    }
  };

  const initializeAllocations = () => {
    // Initialize allocations from line data passed via location state
    if (line) {
      const allocation: RetentionAllocation = {
        id: "ALLOC001",
        workOrderNumber: line.WorkOrder || workOrderId || "",
        billValue: line.BillValue || 0,
        paidTillDate: line.PaidtillDate || 0,
        pendingApproval: line.pendingApproval || 0,
        balance: line.Balance || 0,
        thisPaymentAmount: line.ThisPayment || line.Balance || 0,
      };
      setAllocations([allocation]);
      calculateTotals([allocation]);
    }
  };

  const calculateTotals = (allocs: RetentionAllocation[]) => {
    const sum = allocs.reduce(
      (total, allocation) => total + (allocation.thisPaymentAmount || 0),
      0
    );
    setNetPayable(sum);
  };

  const handleAllocationChange = (
    index: number,
    field: string,
    value: number
  ) => {
    const updatedAllocations = [...allocations];
    updatedAllocations[index] = {
      ...updatedAllocations[index],
      [field]: value,
    };
    setAllocations(updatedAllocations);
    calculateTotals(updatedAllocations);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Validate approvals are set when approval is required
      if (values.approvalRequired === "true" && approvals.data.length === 0) {
        Swal.fire({
          icon: "error",
          title: "Approval Required",
          text: "Please set the required approvals before submitting.",
        });
        return;
      }

      if (allocations.length === 0) {
        message.warning("No retention allocations to release");
        return;
      }

      const hasReleaseAmount = allocations.some((a) => a.thisPaymentAmount > 0);
      if (!hasReleaseAmount) {
        message.warning(
          "Please enter release amount for at least one allocation"
        );
        return;
      }

      setSubmitting(true);

      // Prepare payment lines from allocations
      const lines = allocations
        .filter((allocation) => allocation.thisPaymentAmount > 0)
        .map((allocation) => ({
          workOrderId: workOrderId
            ? typeof workOrderId === "string" && !isNaN(Number(workOrderId))
              ? Number(workOrderId)
              : workOrderId
            : null,
          paidAmount: allocation.thisPaymentAmount,
          remarks: `Retention release for Work Order ${allocation.workOrderNumber}`,
        }));

      // Format approval hierarchy
      const approvalHierarchy = approvals.data.map((approval: any) => ({
        userId: parseInt(approval.userId),
        roleId: approval.roleId,
        level: approval.level,
        approvalStage: approval.approvalStage || "",
      }));

      const paymentPayload = {
        contractorId: parseInt(contractorId),
        ...(paymentSequenceId && {
          paymentSequenceId: paymentSequenceId,
        }),
        paymentMethod: values.paymentMethod,
        paymentType: "Retention",
        paymentDate: values.paymentDate?.format("YYYY-MM-DD"),
        utrNumber: values.utrNumber || null,
        chequeNumber: values.chequeNumber || null,
        transactionNumber: values.transactionNumber || null,
        totalPaymentAmount: netPayable,
        remarks: values.narration || "",
        narration: values.narration || "",
        approvalRequired: values.approvalRequired === "true",
        isDraft: false,
        lines: lines,
        approvalHierarchy:
          values.approvalRequired === "true" ? approvalHierarchy : [],
        flowType: flowType || "Contractor",
        siteId: line?.siteId,
      };

      console.log("Submitting retention release:", paymentPayload);

      // API call
      const formData = new FormData();
      formData.append("payment", JSON.stringify(paymentPayload));

      // Append files if any
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("files", file.originFileObj);
        }
      });

      const apiResponse: any = await paymentApis.createPayment(formData);

      if (apiResponse.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Retention release submitted successfully",
        }).then(() => {
          navigate(-1);
        });
      } else {
        throw new Error(apiResponse.message || "Failed to submit");
      }
    } catch (error: any) {
      console.error("Error submitting retention release:", error);
      if (error?.errorFields) {
        return;
      }
      Swal.fire(
        "Error!",
        error.message || "Failed to submit retention release",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // File upload handlers
  const handleFileUpload = async (file: any) => {
    setUploadLoading(true);
    try {
      const uploadedFile = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: URL.createObjectURL(file),
        originFileObj: file,
      };
      setFileList((prev) => [...prev, uploadedFile]);
      return uploadedFile;
    } catch (error) {
      console.error("File upload failed:", error);
      throw error;
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileRemove = (file: any) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const beforeUpload = (file: any) => {
    const isValidType = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/xlsx",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type);
    if (!isValidType) {
      Swal.fire(
        "Error!",
        "You can only upload JPG/PNG/PDF/DOC/DOCX files!",
        "error"
      );
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      Swal.fire("Error!", "File must be smaller than 10MB!", "error");
      return false;
    }
    return true;
  };

  // Allocation Columns
  const allocationColumns: TableColumnsType<RetentionAllocation> = [
    {
      title: "#",
      key: "#",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Work Order",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 180,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: "Bill Value",
      dataIndex: "billValue",
      key: "billValue",
      width: 130,
      render: (amount: number) => formatCurrency(amount, "INR"),
    },
    {
      title: "Paid (Till Date)",
      dataIndex: "paidTillDate",
      key: "paidTillDate",
      width: 130,
      render: (amount: number) => formatCurrency(amount, "INR"),
    },
    {
      title: "Pending Approval",
      dataIndex: "pendingApproval",
      key: "pendingApproval",
      width: 140,
      render: (amount: number) => formatCurrency(amount, "INR"),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: 130,
      render: (amount: number) => (
        <strong style={{ color: "#52c41a" }}>
          {formatCurrency(amount, "INR")}
        </strong>
      ),
    },
    {
      title: "This Payment",
      dataIndex: "thisPaymentAmount",
      key: "thisPaymentAmount",
      width: 180,
      render: (amount: number, _: any, index: number) => (
        <InputNumber
          value={amount}
          min={0}
          max={allocations[index]?.balance || 0}
          formatter={indianCurrencyFormatter}
          parser={indianCurrencyParser}
          onChange={(value) =>
            handleAllocationChange(index, "thisPaymentAmount", value || 0)
          }
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  return (
    <>
      <Head title="Release Retention" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg">
            <BlockHead size="lg">
              <BlockBetween>
                <BlockHeadContent>
                  <BlockTitle tag="h4">Release Retention Amount</BlockTitle>
                </BlockHeadContent>
                <div className="d-flex">
                  <div className="actions clearfix">
                    <ul
                      className="m-auto d-flex align-items-center justify-content-center p-0"
                      style={{ flexDirection: "row-reverse" }}
                    >
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          onClick={() => navigate(-1)}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>
              </BlockBetween>
            </BlockHead>

            <div className="content clearfix">
              <Form form={form} layout="vertical" size="large">
                {/* Basic Information */}
                <Block>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item
                        name="paymentDate"
                        label="Payment Date"
                        rules={[
                          {
                            required: true,
                            message: "Payment date is required",
                          },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="paymentType" label="Payment Type">
                        <Select disabled>
                          <Option value={PaymentType.RetentionRelease}>
                            Release Retention Amount
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* Payment Voucher Number - Show only when sequence is not configured */}
                    {((sequenceAccess?.level === "Site" &&
                      !sequenceAccess?.siteIds?.includes(
                        line?.siteId?.toString()
                      )) ||
                      sequenceAccess?.module === "") && (
                      <Col span={8}>
                        <Form.Item
                          name="paymentVoucherNumber"
                          label="Payment Voucher Number"
                          rules={[
                            {
                              required: true,
                              message: "Payment Voucher Number is required",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter Payment Voucher Number"
                            value={paymentSequenceId}
                            onChange={(e) =>
                              setPaymentSequenceId(e.target.value)
                            }
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        name="contractorName"
                        label={
                          flowType?.toLowerCase() === "client"
                            ? "Client"
                            : "Contractor"
                        }
                      >
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        name="paymentMethod"
                        label="Payment Method"
                        rules={[
                          {
                            required: true,
                            message: "Payment method is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select payment method">
                          {mockPaymentMethodOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Conditional fields based on Payment Method */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.paymentMethod !== currentValues.paymentMethod
                      }
                    >
                      {({ getFieldValue }) => {
                        const paymentMethod = getFieldValue("paymentMethod");

                        if (
                          paymentMethod === PaymentMethod.NEFT ||
                          paymentMethod === PaymentMethod.RTGS ||
                          paymentMethod === PaymentMethod.IMPS
                        ) {
                          return (
                            <Col span={6}>
                              <Form.Item
                                name="utrNumber"
                                label="UTR Number"
                                rules={[
                                  {
                                    required: true,
                                    message: "UTR number is required",
                                  },
                                ]}
                              >
                                <Input placeholder="Enter UTR number" />
                              </Form.Item>
                            </Col>
                          );
                        }

                        if (paymentMethod === PaymentMethod.Cheque) {
                          return (
                            <Col span={6}>
                              <Form.Item
                                name="chequeNumber"
                                label="Cheque Number"
                                rules={[
                                  {
                                    required: true,
                                    message: "Cheque number is required",
                                  },
                                ]}
                              >
                                <Input placeholder="Enter cheque number" />
                              </Form.Item>
                            </Col>
                          );
                        }

                        if (paymentMethod === PaymentMethod.UPI) {
                          return (
                            <Col span={6}>
                              <Form.Item
                                name="transactionNumber"
                                label="Transaction Number"
                                rules={[
                                  {
                                    required: true,
                                    message: "Transaction number is required",
                                  },
                                ]}
                              >
                                <Input placeholder="Enter transaction number" />
                              </Form.Item>
                            </Col>
                          );
                        }

                        return null;
                      }}
                    </Form.Item>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={18}>
                      <Form.Item name="narration" label="Narration">
                        <TextArea
                          rows={2}
                          placeholder="Enter payment narration"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* Retention Allocations */}
                <Block>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Retention Allocations</h5>
                  </div>

                  {allocations.length === 0 ? (
                    <div className="text-center py-4">
                      <Empty description="No retention allocations available." />
                    </div>
                  ) : (
                    <Table
                      columns={allocationColumns}
                      dataSource={allocations}
                      rowKey="id"
                      scroll={{ x: 1100 }}
                      bordered
                      size="small"
                      pagination={false}
                    />
                  )}
                </Block>

                {/* Approval Settings */}
                <Divider />
                <Block>
                  <h5>Approval Settings</h5>
                  <Row gutter={[16, 16]}>
                    <Col span={6}>
                      <Form.Item
                        name="approvalRequired"
                        label="Approval Required ?"
                        rules={[
                          {
                            required: true,
                            message: "Please specify if approval is required",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select"
                          onChange={(value: string) => {
                            setApprovalRequired(value);
                            if (value === "false") {
                              setApprovals({ open: false, data: [] });
                            }
                          }}
                        >
                          <Option value="true">Yes</Option>
                          <Option value="false">No</Option>
                        </Select>
                      </Form.Item>

                      {approvalRequired === "true" && (
                        <Popover
                          content="Click here to set required approvals"
                          title="Approval Required"
                        >
                          <a
                            href="#"
                            className="success"
                            onClick={(e) => {
                              e.preventDefault();
                              setApprovals((prev) => ({
                                ...prev,
                                open: true,
                              }));
                            }}
                          >
                            <EditOutlined /> Set Approvals &nbsp;
                            {approvals?.data?.length === 0 ? (
                              <span className="mandatory">
                                Please set approvals
                              </span>
                            ) : (
                              <span className="text-success">
                                {approvals.data.length} approval(s) configured
                              </span>
                            )}
                          </a>
                        </Popover>
                      )}
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* File Upload Section */}
                <Block>
                  <h5>Supporting Documents</h5>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="attachments"
                        label="Upload Documents"
                        help="Supported formats: JPG, PNG, PDF, XLSX, DOC, DOCX (Max size: 10MB each)"
                      >
                        <Upload
                          name="file"
                          listType="text"
                          fileList={fileList}
                          beforeUpload={beforeUpload}
                          customRequest={async ({
                            file,
                            onSuccess,
                            onError,
                          }) => {
                            try {
                              const uploadedFile = await handleFileUpload(file);
                              onSuccess && onSuccess(uploadedFile);
                            } catch (error) {
                              onError && onError(error as any);
                            }
                          }}
                          onRemove={handleFileRemove}
                          multiple
                        >
                          <AntButton
                            icon={<UploadOutlined />}
                            loading={uploadLoading}
                            disabled={uploadLoading}
                          >
                            {uploadLoading ? "Uploading..." : "Upload Files"}
                          </AntButton>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* Action Buttons */}
                <div className="d-flex justify-content-end">
                  <Space>
                    <Button color="secondary" onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={submitting || allocations.length === 0}
                    >
                      {submitting ? <Spinner size="sm" /> : <></>}
                      Submit
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          </div>
        </Card>

        {/* Payments Approval Drawer */}
        {approvals.open && (
          <PaymentsApprovalDrawer
            open={approvals.open}
            siteId={line?.siteId || 0}
            onClose={() => {
              setApprovals((prev) => ({
                ...prev,
                open: false,
              }));
            }}
            onSubmit={(data: any) => {
              setApprovals({
                open: false,
                data: data,
              });
            }}
            setApprovalSubmissionStatus={(status: boolean) => {}}
            approvalData={approvals.data}
          />
        )}
      </Content>
    </>
  );
};

export default ReleaseRetentionForm;
